import React from 'react';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { useAuth } from '../../Contexts/AuthProvider';
import exponentLogoWhite from '../../Exponent_Logo_White.png';

export const HEADER_HEIGHT = 44;

interface AppHeaderProps {
  onMenuToggle?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ onMenuToggle }) => {
  const { user } = useAuth();

  const displayName = user?.displayName?.trim() || 'User';
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U';

  return (
    <Box
      component="header"
      sx={{
        height: HEADER_HEIGHT,
        minHeight: HEADER_HEIGHT,
        bgcolor: appColors.header.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 1.5, sm: 2.5 },
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        zIndex: 1300,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
      }}
    >
      {/* ── Left: hamburger (mobile) + Exponent logo + app name ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
        {onMenuToggle && (
          <IconButton
            aria-label="Open navigation"
            onClick={onMenuToggle}
            size="small"
            sx={{
              color: appColors.header.text,
              display: { xs: 'flex', md: 'none' },
              mr: -0.5,
            }}
          >
            <MenuIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
        <Box
          component="img"
          src={exponentLogoWhite}
          alt="Exponent"
          sx={{ height: { xs: 18, sm: 24 }, flexShrink: 0 }}
        />
        <Box
          sx={{
            width: '1px',
            height: 20,
            bgcolor: appColors.header.border,
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: appColors.header.text,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Emergency Evacuation Accountability System (EEAS)
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: appColors.header.text,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            display: { xs: 'block', sm: 'none' },
          }}
        >
          EEAS
        </Typography>
      </Box>

      {/* ── Right: user ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: appColors.header.text,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {displayName}
        </Typography>
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: 'rgba(255,255,255,0.2)',
            color: appColors.header.text,
            fontSize: '0.75rem',
            fontWeight: 700,
          }}
        >
          {initials}
        </Avatar>
      </Box>
    </Box>
  );
};

export default AppHeader;
